import React, { useLayoutEffect, useRef, useState } from "react";
import { showToast } from "./errors";
import { useMyTimer, useElapsedTime } from "../context/TimerContext";
import TimerSwitchModal from "./TimerSwitchModal";
import { timeCategory } from "../utils/timeCategories";
import "./TimerBar.css";

const TimerBar = () => {
  const { activeTimer, canTrackTime, clockOut, pending } = useMyTimer();
  const [showSwitch, setShowSwitch] = useState(false);
  const [clockOutNote, setClockOutNote] = useState("");
  const [showNoteInput, setShowNoteInput] = useState(false);

  const elapsed = useElapsedTime(activeTimer?.clock_in);

  // The pages keep room for the bar while it shows (--timer-bar-space:
  // TimerBar.css .page-content::after, the order page's full-height panels), so the last of a
  // page — the order's totals and New Payment — never sits under it.
  const barRef = useRef(null);
  const shown = !!(canTrackTime && activeTimer);
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = barRef.current;
    if (!shown || !el) return undefined;
    const measure = () => root.style.setProperty("--timer-bar-space", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      root.style.removeProperty("--timer-bar-space");
    };
  }, [shown]);

  if (!shown) return null;

  const cat = timeCategory(activeTimer.category);
  const orderLabel = activeTimer.order_number
    ? `Order #${activeTimer.order_number}`
    : "General";
  const serviceLabel = activeTimer.service_name ? ` · ${activeTimer.service_name}` : "";

  const handleClockOut = async () => {
    try {
      await clockOut({ note: clockOutNote || undefined });
      setClockOutNote("");
      setShowNoteInput(false);
    } catch (err) {
      showToast.error("Couldn't clock out: " + err.message);
    }
  };

  return (
    <>
      <div className="timer-bar" ref={barRef} style={{ borderTopColor: cat.color }}>
        <div className="timer-bar-inner">
          <div className="timer-bar-left">
            <span className="timer-bar-category" style={{ background: cat.bg, color: cat.color }}>
              {cat.label}
            </span>
            <span className="timer-bar-label">
              <strong>{orderLabel}</strong>
              {serviceLabel}
            </span>
            {/* Clocked in with no signal: it's kept on this device and goes by itself. */}
            {activeTimer.waiting && <span className="timer-bar-waiting" role="status">Waiting for signal</span>}
          </div>

          <div className="timer-bar-elapsed">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            {elapsed}
          </div>

          <div className="timer-bar-actions">
            {showNoteInput && (
              <input
                className="timer-bar-note"
                placeholder="Add note..."
                value={clockOutNote}
                onChange={(e) => setClockOutNote(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleClockOut(); }}
                autoFocus
              />
            )}
            <button
              className="timer-bar-btn timer-bar-btn-switch"
              onClick={() => setShowSwitch(true)}
              disabled={pending}
              title="Switch to different activity"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
              Switch
            </button>
            <button
              className="timer-bar-btn timer-bar-btn-clockout"
              onClick={() => {
                if (showNoteInput) handleClockOut();
                else setShowNoteInput(true);
              }}
              disabled={pending}
            >
              {pending ? "..." : showNoteInput ? "Stop" : "Clock Out"}
            </button>
          </div>
        </div>
      </div>

      {showSwitch && (
        <TimerSwitchModal
          currentTimer={activeTimer}
          onClose={() => setShowSwitch(false)}
        />
      )}
    </>
  );
};

export default React.memo(TimerBar);
